import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import errorMessageFromResponse, { isApiErrorMessage } from './errorMessageFromResponse';

/**
 * What Sentry is told about a failed API request. Deliberately no request or response bodies:
 * they can carry campaign and customer data. The API's own message, the validation field names
 * (not their values) and the query parameter names (not their values) are enough to find the cause.
 */
export type ApiRequestFailure = {
    method: string;
    /** URL path with numeric ids and UUIDs replaced by `:id`, so one endpoint is one Sentry issue. */
    route: string;
    queryKeys: string[];
    status: number | null;
    statusText: string | null;
    /** axios's code: ERR_NETWORK, ECONNABORTED, ERR_CANCELED, ERR_BAD_RESPONSE, ... */
    code: string | null;
    /** Aborted by our own code (an AbortController or a newer request replacing this one). */
    cancelled: boolean;
    /** `status`, or `cancelled` / `timeout` / `network error` when there was no response. */
    outcome: string;
    /**
     * meta.error, message or debug from the response; null when the API gave none. `debug` is
     * here only: `errorMessageFromResponse` keeps it from users.
     */
    apiMessage: string | null;
    validationFields: string[];
};

const MAX_MESSAGE_LENGTH = 300;

const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

const parseUrl = (url: string) => {
    try {
        return new URL(url.trim(), 'http://relative.invalid');
    } catch {
        return null;
    }
};

const decode = (segment: string) => {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

const routeOf = (url: URL | null) =>
    url == null
        ? 'unknown'
        : url.pathname
              .split('/')
              .map(segment => decode(segment).trim())
              .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
              .join('/');

const outcomeOf = ({
    status,
    code,
    cancelled,
}: Pick<ApiRequestFailure, 'status' | 'code' | 'cancelled'>) => {
    if (status != null) return String(status);
    if (cancelled) return 'cancelled';
    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return 'timeout';
    return 'network error';
};

export function describeApiFailure(error: AxiosError<any>): ApiRequestFailure {
    const url = parseUrl(error.config?.url ?? '');
    const status = error.response?.status ?? null;
    const code = error.code ?? null;
    const cancelled = code === 'ERR_CANCELED';
    const message = errorMessageFromResponse(error);
    const debug = error.response?.data?.debug;
    const apiMessage = isApiErrorMessage(message)
        ? message
        : typeof debug === 'string' && debug.trim() !== ''
        ? debug
        : null;
    const validation = error.response?.data?.validation;

    return {
        method: (error.config?.method ?? 'get').toUpperCase(),
        route: routeOf(url),
        queryKeys: url == null ? [] : [...new Set(url.searchParams.keys())],
        status,
        statusText: error.response?.statusText || null,
        code,
        cancelled,
        outcome: outcomeOf({ status, code, cancelled }),
        apiMessage: apiMessage?.slice(0, MAX_MESSAGE_LENGTH) ?? null,
        validationFields:
            validation != null && typeof validation === 'object' ? Object.keys(validation) : [],
    };
}

/** e.g. `POST /api/displays/screen/frame-search → 403 Forbidden: This action is unauthorized.` */
export const apiFailureTitle = (failure: ApiRequestFailure) =>
    [
        `${failure.method} ${failure.route} → ${failure.outcome}`,
        failure.statusText ? ` ${failure.statusText}` : '',
        failure.apiMessage ? `: ${failure.apiMessage}` : '',
    ].join('');

/**
 * Thrown by `useAPIClientRequest` when a request fails. `message` is the same string the hook puts
 * in its `error` state (callers display it), so the request details travel separately, in
 * `failure`, for Sentry to read.
 */
export class ApiRequestError extends Error {
    readonly failure: ApiRequestFailure;

    constructor(message: string, failure: ApiRequestFailure) {
        super(message);
        this.name = 'ApiRequestError';
        this.failure = failure;
    }
}

/** The request details behind an exception, if it is a failed API request. */
export const apiFailureOf = (exception: unknown): ApiRequestFailure | null => {
    if (exception instanceof ApiRequestError) return exception.failure;
    if (isAxiosError(exception)) return describeApiFailure(exception);
    return null;
};
